import { useEffect, useRef, useState, useCallback } from 'react';
import { env } from '@/lib/config/env';
import { useAuthStore } from '@/store/authStore';
import { useRealtimeStore } from '@/store/realtimeStore';
import { httpClient } from '@/lib/api/client';
import { ROUTES } from '@/lib/config/routes';

interface WSMessage {
  event: string;
  data: unknown;
  timestamp: number;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  lastMessage: WSMessage | null;
}

function getWsUrl(): string {
  const base = env.apiBaseUrl;
  const wsBase = base.replace(/^http/, 'ws').replace(/\/api\/v1\/?$/, '');
  return `${wsBase}/ws`;
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

function forceLogout() {
  useAuthStore.getState().clearSession();
  if (window.location.pathname !== ROUTES.login) {
    window.location.replace(ROUTES.login);
  }
}

export function useWebSocket(): UseWebSocketReturn {
  const [isConnected, setIsConnectedLocal] = useState(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setStoreConnected = useRealtimeStore((s) => s.setConnected);
  const [lastMessage, setLastMessage] = useState<WSMessage | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>();
  const unmounted = useRef(false);

  const setIsConnected = useCallback(
    (val: boolean) => {
      setIsConnectedLocal(val);
      setStoreConnected(val);
    },
    [setStoreConnected],
  );

  const connect = useCallback(async () => {
    if (unmounted.current) return;

    let token = useAuthStore.getState().token;
    if (!token) return;

    if (isTokenExpired(token)) {
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      token = await httpClient.refreshToken();
      if (unmounted.current) return;
      if (!token) {
        forceLogout();
        return;
      }
    }

    const url = `${getWsUrl()}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (unmounted.current) {
        ws.close();
        return;
      }
      setIsConnected(true);
      reconnectAttempt.current = 0;
    };

    ws.onmessage = (e) => {
      try {
        const msg: WSMessage = JSON.parse(e.data as string);
        setLastMessage(msg);
      } catch {}
    };

    ws.onclose = () => {
      setIsConnected(false);
      wsRef.current = null;
      if (unmounted.current) return;

      const currentToken = useAuthStore.getState().token;
      if (!currentToken) {
        forceLogout();
        return;
      }

      const delay = Math.min(1000 * Math.pow(2, reconnectAttempt.current), 30_000);
      reconnectAttempt.current += 1;
      reconnectTimer.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {};
  }, [setIsConnected]);

  useEffect(() => {
    unmounted.current = false;
    if (isAuthenticated) connect();

    const onOnline = () => {
      if (isAuthenticated && !unmounted.current && !wsRef.current) {
        clearTimeout(reconnectTimer.current);
        reconnectAttempt.current = 0;
        connect();
      }
    };
    window.addEventListener('online', onOnline);

    return () => {
      unmounted.current = true;
      window.removeEventListener('online', onOnline);
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect, isAuthenticated]);

  return { isConnected, lastMessage };
}
